import { z } from 'zod';
import { ExperienceType } from '../../enums/experience';

export type MediaDto = { id: string; src?: string; thumbnail?: string };

export type StepWithTags =
    | 'overall'
    | 'cleanliness'
    | 'accuracy'
    | 'communication'
    | 'access'
    | 'value';

export type TagSet = {
    positive: readonly string[];
    negative: readonly string[];
};
export type TagsByStep = Record<StepWithTags, TagSet>;

export type StepKey =
    | 'overall'
    | 'cleanliness'
    | 'accuracy'
    | 'communication'
    | 'access'
    | 'value'
    | 'details'
    | 'review'
    | 'submitted';

export type StepMeta = { key: StepKey; title: string; subtitle?: string };

export const OTHER_SENTINEL = '__other';
export const LONG_MIN = 20;
export const LONG_MAX = 2000;
export const OTHER_MAX = 100;
export const MAX_FILES = 50;
export const MAX_FILE_SIZE = 5_000_000;

const categoryReviewSchema = z.object({
    rating: z.number().min(1, 'Required').max(5),
    positives: z.array(z.string()).default([]),
    negatives: z.array(z.string()).default([]),
    otherTextPositive: z
        .string()
        .max(OTHER_MAX, `Max ${OTHER_MAX} characters`)
        .default('')
        .transform((s) => (s ? s.trim() : s)),
    otherTextNegative: z
        .string()
        .max(OTHER_MAX, `Max ${OTHER_MAX} characters`)
        .default('')
        .transform((s) => (s ? s.trim() : s)),
});

const optionalCategoryReviewSchema = categoryReviewSchema.optional();

const reviewSchema = z.object({
    rating: z.number().int().min(1, 'Required').max(5),
    reviewText: z
        .string()
        .min(LONG_MIN, `Min ${LONG_MIN} characters`)
        .max(LONG_MAX, `Max ${LONG_MAX} characters`),
    photos: z.any().optional(),
    access: optionalCategoryReviewSchema,
    accuracy: optionalCategoryReviewSchema,
    cleanliness: optionalCategoryReviewSchema,
    communication: optionalCategoryReviewSchema,
    value: optionalCategoryReviewSchema,
    isAnonymous: z.boolean().default(false),
});

export type ReviewFormValues = z.infer<typeof reviewSchema>;

export const buildReviewSchema = (type: ExperienceType) => {
    const baseShape = {
        rating: z.number().int().min(1, 'Required').max(5),
        reviewText: z
            .string()
            .min(LONG_MIN, `Min ${LONG_MIN} characters`)
            .max(LONG_MAX, `Max ${LONG_MAX} characters`),
        photos: z.any().optional(),
        access: optionalCategoryReviewSchema,
        accuracy: optionalCategoryReviewSchema,
        communication: optionalCategoryReviewSchema,
        value: optionalCategoryReviewSchema,
        isAnonymous: z.boolean().default(false),
    };

    if (type !== ExperienceType.Activity) {
        (baseShape as any).cleanliness = optionalCategoryReviewSchema;
    }

    return z.object(baseShape);
};

export const EMPTY_TAGSET: TagSet = { positive: [], negative: [] };
export const EMPTY_TAGS_BY_STEP: TagsByStep = {
    overall: EMPTY_TAGSET,
    cleanliness: EMPTY_TAGSET,
    accuracy: EMPTY_TAGSET,
    communication: EMPTY_TAGSET,
    access: EMPTY_TAGSET,
    value: EMPTY_TAGSET,
};

export { categoryReviewSchema, optionalCategoryReviewSchema, reviewSchema };
